<template>
  <transition
    name="dialog-fade"
    @after-enter="afterEnter"
    @after-leave="afterLeave"
  >
    <div class="zm-dialog__wrapper" v-show="visible" @click.self="handleWrapperClick">
      <div
        class="zm-dialog"
        :class="[{ 'is-fullscreen': fullscreen, 'zm-dialog--center': center }, customClass]"
        ref="dialog"
        role="dialog"
        :aria-describedby="(visible && ariaDescribedby) ? ariaDescribedby : null"
        :aria-labelledby="visible ? (ariaLabelledby ? ariaLabelledby : dialogId) : null"
        v-bind="$attrs"
        :style="style"
      >
        <div class="zm-dialog__header">
          <slot name="title">
            <span class="zm-dialog__title" :id="dialogId">{{ title }}</span>
          </slot>
          <button
            type="button"
            class="zm-dialog__headerbtn"
            aria-label="Close"
            v-if="showClose"
            @click="handleClose"
          >
            <i class="zm-dialog__close zm-icon zm-icon-close"></i>
          </button>
        </div>
        <div class="zm-dialog__body" v-if="rendered">
          <slot></slot>
        </div>
        <div class="zm-dialog__footer" v-if="$slots.footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </transition>
</template>

<script type="text/babel">
  import Popup from '../../utils/popup';
  import emitter from '../../mixins/emitter';
  import { generateId } from '../../utils/util';
  import AriaDialog from '../../utils/aria-dialog';

  export default {
    name: 'ZmDialog',

    mixins: [Popup, emitter],

    props: {
      title: {
        type: String,
        default: ''
      },

      modal: {
        type: Boolean,
        default: true
      },

      modalAppendToBody: {
        type: Boolean,
        default: true
      },

      appendToBody: {
        type: Boolean,
        default: false
      },

      lockScroll: {
        type: Boolean,
        default: true
      },

      closeOnClickModal: {
        type: Boolean,
        default: false
      },

      closeOnPressEscape: {
        type: Boolean,
        default: true
      },

      showClose: {
        type: Boolean,
        default: false
      },

      width: String,

      fullscreen: Boolean,

      customClass: {
        type: String,
        default: ''
      },

      top: {
        type: String,
        default: '15vh'
      },
      beforeClose: Function,
      center: {
        type: Boolean,
        default: false
      },
      ariaDescribedby: {
        type: String,
        default: ''
      },
      ariaLabelledby: {
        type: String,
        default: ''
      }
    },
    inheritAttrs: false,
    data() {
      return {
        closed: false,
        dialogId: `dialog-title-${generateId()}-${generateId()}`
      };
    },

    watch: {
      visible(val) {
        if (val) {
          this.closed = false;
          this.$emit('open');
          this.$el.addEventListener('scroll', this.updatePopper);
          this.$nextTick(() => {
            this.$refs.dialog.scrollTop = 0;
          });
          if (this.appendToBody) {
            document.body.appendChild(this.$el);
          }
        } else {
          this.$el.removeEventListener('scroll', this.updatePopper);
          if (!this.closed) this.$emit('close');
        }
      }
    },

    computed: {
      style() {
        let style = {};
        if (!this.fullscreen) {
          style.marginTop = this.top;
          if (this.width) {
            style.width = this.width;
          }
        }
        return style;
      }
    },

    methods: {
      getFirstFocus() {
        const focusFirst = this.$el.querySelector('[focusFirst]') || this.$el.querySelector('[focus-first]');
        if (focusFirst) {
          return focusFirst;
        }
        return null;
      },
      handleWrapperClick() {
        if (!this.closeOnClickModal) return;
        this.handleClose();
      },
      handleClose() {
        if (typeof this.beforeClose === 'function') {
          this.beforeClose(this.hide);
        } else {
          this.hide();
        }
      },
      hide(cancel) {
        if (cancel !== false) {
          this.$emit('update:visible', false);
          this.$emit('close');
          this.closed = true;
        }
      },
      updatePopper() {
        this.broadcast('ZmSelectDropdown', 'updatePopper');
        this.broadcast('ZmDropdownMenu', 'updatePopper');
      },
      afterEnter() {
        this.$emit('opened');
        this.setFirstFocus();
      },
      afterLeave() {
        this.$emit('closed');
        this.setFocusAfterClosed();
      },
      refreshAria() {
        this.$nextTick(() => {
          this.ariaDialog && this.ariaDialog.refresh();
        });
      },
      setFirstFocus() {
        const focusAfterClosed = document.activeElement;
        const firstFocus = this.getFirstFocus();
        this.$nextTick(() => {
          this.ariaDialog = new AriaDialog(this.$refs.dialog, focusAfterClosed, firstFocus);
        });
      },
      setFocusAfterClosed() {
        this.ariaDialog && this.ariaDialog.closeDialog();
        this.ariaDialog = null;
      }
    },

    mounted() {
      if (this.visible) {
        this.rendered = true;
        this.open();
        if (this.appendToBody) {
          document.body.appendChild(this.$el);
        }
        this.setFirstFocus();
      }
    },

    destroyed() {
      this.setFocusAfterClosed();
      // if appendToBody is true, remove DOM node after destroy
      if (this.appendToBody && this.$el && this.$el.parentNode) {
        this.$el.parentNode.removeChild(this.$el);
      }
      this.ariaDialog = null;
    }
  };
</script>

<style lang="scss">
@import "./style";
</style>
